import type { DepartmentModel } from '@app/reducers/orm/department/department.model';
import type { EmployeeModel } from '@app/reducers/orm/employee/employee.model';
import type { LocationModel } from '@app/reducers/orm/location/location.model';
import type { TeamModel } from '@app/reducers/orm/team/team.model';
import type { Dictionary } from '@ngrx/entity';

export type OrganizationStatus = 'active' | 'inactive';

export interface TeamRow {
  team: TeamModel;
  inactive: boolean;
}

export interface DepartmentRow {
  department: DepartmentModel;
  inactive: boolean;
  teams: TeamRow[];
  /**
   * How many active teams the department has, unaffected by search and status filtering. The
   * delete action is disabled on a department's last active team, and that is a property of the
   * department rather than of whatever the list currently shows.
   */
  activeTeamCount: number;
}

export interface LocationRow {
  location: LocationModel;
  inactive: boolean;
  departments: DepartmentRow[];
}

export interface OrganizationRowsInput {
  locations: LocationModel[];
  departmentsByLocation: Dictionary<DepartmentModel[]>;
  teamsByDepartment: Dictionary<TeamModel[]>;
  query: string;
  statuses: ReadonlySet<OrganizationStatus>;
}

const nameMatches = (name: string, query: string): boolean => name.toLowerCase().includes(query);

/**
 * Builds the location → department → team rows the list renders.
 *
 * Deactivation cascades down: a department under a deactivated location reads as inactive even when
 * its own `deleted` flag is unset, and so does a team under such a department. The backend leaves
 * those flags untouched, so the cascade has to happen here or a deactivated location would render
 * an active-looking subtree.
 *
 * A row survives when it matches both filters, or when any descendant does — a parent kept only as
 * context for a matching child still renders, which is what keeps a matched team reachable under a
 * location whose own name matches nothing.
 */
export function buildOrganizationRows({
  locations,
  departmentsByLocation,
  teamsByDepartment,
  query,
  statuses,
}: OrganizationRowsInput): LocationRow[] {
  const normalizedQuery = query.trim().toLowerCase();

  const statusMatches = (inactive: boolean): boolean => statuses.has(inactive ? 'inactive' : 'active');

  const locationRows: LocationRow[] = [];

  for (const location of locations) {
    const locationInactive = !!location.deleted;
    // An ancestor whose name matches satisfies the search for everything beneath it, so searching
    // for a location shows its contents rather than the location row on its own. The status filter
    // still applies to each of those rows individually.
    const locationMatchesQuery = !normalizedQuery || nameMatches(location.name, normalizedQuery);

    const departmentRows: DepartmentRow[] = [];

    for (const department of departmentsByLocation[location.id] ?? []) {
      const departmentInactive = locationInactive || !!department.deleted;
      const departmentMatchesQuery = locationMatchesQuery || nameMatches(department.name, normalizedQuery);

      const departmentTeams = teamsByDepartment[department.id] ?? [];
      const teamRows: TeamRow[] = [];

      for (const team of departmentTeams) {
        const teamInactive = departmentInactive || !!team.deleted;
        const teamMatchesQuery = departmentMatchesQuery || nameMatches(team.name, normalizedQuery);

        if (statusMatches(teamInactive) && teamMatchesQuery) {
          teamRows.push({ team, inactive: teamInactive });
        }
      }

      if ((statusMatches(departmentInactive) && departmentMatchesQuery) || teamRows.length > 0) {
        departmentRows.push({
          department,
          inactive: departmentInactive,
          teams: teamRows,
          activeTeamCount: departmentTeams.length,
        });
      }
    }

    if ((statusMatches(locationInactive) && locationMatchesQuery) || departmentRows.length > 0) {
      locationRows.push({ location, inactive: locationInactive, departments: departmentRows });
    }
  }

  return locationRows;
}

/**
 * Counts the departments that are active in their own right — the delete action is disabled on the
 * last one, and that limit is a property of the account, not of whatever the list is filtered to.
 */
export function countActiveDepartments(
  locations: LocationModel[],
  departmentsByLocation: Dictionary<DepartmentModel[]>,
): number {
  return locations
    .filter((location) => !location.deleted)
    .reduce(
      (total, location) =>
        total + (departmentsByLocation[location.id] ?? []).filter((department) => !department.deleted).length,
      0,
    );
}

/**
 * Locations that still have employees assigned to their teams. Their delete action is disabled: the
 * backend rejects deleting a location whose departments still hold employees — they must be
 * relocated first.
 */
export function computeLocationsWithEmployees(
  employees: EmployeeModel[],
  departmentsByLocation: Dictionary<DepartmentModel[]>,
  teamsByDepartment: Dictionary<TeamModel[]>,
): Set<string> {
  const departmentToLocation = new Map<string, string>();
  for (const [locationId, departmentList] of Object.entries(departmentsByLocation)) {
    for (const department of departmentList ?? []) {
      // Only active departments block deletion — a deactivated department has already had its
      // employees relocated, so it should no longer flag its location as occupied.
      if (department.deleted) {
        continue;
      }

      departmentToLocation.set(department.id, locationId);
    }
  }

  const teamToLocation = new Map<string, string>();
  for (const teamList of Object.values(teamsByDepartment)) {
    for (const team of teamList ?? []) {
      const locationId = departmentToLocation.get(team.department_id);
      if (locationId) {
        teamToLocation.set(team.id, locationId);
      }
    }
  }

  const locationsWithEmployees = new Set<string>();
  for (const employee of employees) {
    for (const teamId of employee.Team ?? []) {
      const locationId = teamToLocation.get(teamId);
      if (locationId) {
        locationsWithEmployees.add(locationId);
      }
    }
  }

  return locationsWithEmployees;
}
